// API service for connecting to the backend

const BACKEND_HOST =
  process.env.REACT_APP_BACKEND_HOST || 'http://localhost';

const BACKEND_PORT =
  process.env.REACT_APP_BACKEND_PORT && process.env.REACT_APP_BACKEND_PORT.trim()
    ? process.env.REACT_APP_BACKEND_PORT.trim()
    : null;

let API_BASE_URL;

if (BACKEND_HOST.startsWith('http')) {
  // Host already has protocol (prod or local)
  if (BACKEND_PORT) {
    const url = new URL(BACKEND_HOST);
    url.port = BACKEND_PORT;
    API_BASE_URL = url.origin;
  } else {
    // IMPORTANT: do NOT add port if not explicitly set
    API_BASE_URL = new URL(BACKEND_HOST).origin;
  }
} else {
  // Host without protocol (rare, but safe)
  if (BACKEND_PORT) {
    API_BASE_URL = `http://${BACKEND_HOST}:${BACKEND_PORT}`;
  } else {
    API_BASE_URL = `http://${BACKEND_HOST}`;
  }
}

export { API_BASE_URL };


// Helper function to get auth headers
function getAuthHeaders() {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

// Helper function to clean deprecated fields from feature/subfeature/attribute data
function cleanData(data) {
  const {
    location,
    purpose,
    process_workflow,
    associated_components,
    min_value,
    max_value,
    possible_values,
    is_detailed_extracted,
    created_at,
    updated_at,
    ...cleanData
  } = data;
  return cleanData;
}

export const apiService = {
  // Unified processing API call
  async processContent(formData) {
    try {
      // For file uploads, we don't set Content-Type header as it will be set automatically with boundary
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type']; // Remove content-type to let browser set it with boundary

      const response = await fetch(`${API_BASE_URL}/api/process`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text(); // Get error response text
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error processing content:', error);
      throw error;
    }
  },

  // Deprecated individual upload methods - keeping aliases for backward compatibility if needed, 
  // but they should be refactored to use processContent or removed.
  // For now, I will remove them to enforce usage of the new endpoint.


  // Codebase processing API calls
  async getFeaturesByProject(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/features/project/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching features:', error);
      throw error;
    }
  },

  async getSubfeaturesByFeature(featureId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subfeatures/feature/${featureId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching subfeatures:', error);
      throw error;
    }
  },

  async getStoredFeaturesWithDetails(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stored_features_with_details/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching stored features with details:', error);
      throw error;
    }
  },

  async getHasDetailedExtraction(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/has_detailed_extraction/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error checking detailed extraction:', error);
      throw error;
    }
  },

  async getAttributesByProject(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attributes/project/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching attributes:', error);
      throw error;
    }
  },

  async getStoredAttributesWithDetails(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stored_attributes_with_details/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching stored attributes with details:', error);
      throw error;
    }
  },

  async getProjects() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  async deleteProject(projectName) {
    try {
      // Properly encode the project name for the URL
      const encodedProjectName = encodeURIComponent(projectName);
      const response = await fetch(`${API_BASE_URL}/api/delete_project/${encodedProjectName}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  async addFeature(featureData) {
    try {
      const cleanFeatureData = cleanData(featureData);
      const response = await fetch(`${API_BASE_URL}/api/features`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanFeatureData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding feature:', error);
      throw error;
    }
  },

  async updateFeature(featureId, featureData) {
    try {
      const cleanFeatureData = cleanData(featureData);
      const response = await fetch(`${API_BASE_URL}/api/features/${featureId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanFeatureData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating feature:', error);
      throw error;
    }
  },

  async deleteFeature(featureId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/features/${featureId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting feature:', error);
      throw error;
    }
  },

  async addSubfeature(subfeatureData) {
    try {
      const cleanSubfeatureData = cleanData(subfeatureData);
      const response = await fetch(`${API_BASE_URL}/api/subfeatures`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanSubfeatureData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding subfeature:', error);
      throw error;
    }
  },

  async updateSubfeature(subfeatureId, subfeatureData) {
    try {
      const cleanSubfeatureData = cleanData(subfeatureData);
      const response = await fetch(`${API_BASE_URL}/api/subfeatures/${subfeatureId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanSubfeatureData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating subfeature:', error);
      throw error;
    }
  },

  async deleteSubfeature(subfeatureId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/subfeatures/${subfeatureId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting subfeature:', error);
      throw error;
    }
  },

  async updateAttribute(attributeId, attributeData) {
    try {
      const cleanAttributeData = cleanData(attributeData);
      const response = await fetch(`${API_BASE_URL}/api/attributes/${attributeId}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanAttributeData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating attribute:', error);
      throw error;
    }
  },

  async deleteAttribute(attributeId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attributes/${attributeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting attribute:', error);
      throw error;
    }
  },

  async addAttribute(attributeData) {
    try {
      const cleanAttributeData = cleanData(attributeData);
      const response = await fetch(`${API_BASE_URL}/api/attributes`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanAttributeData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding attribute:', error);
      throw error;
    }
  },



  async checkDetailedExtraction(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/has_detailed_extraction/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error checking detailed extraction:', error);
      throw error;
    }
  },

  async getTaskStatus(taskId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting task status:', error);
      throw error;
    }
  },

  async extractFeatures(projectId, project_name) {
    try {
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type'];

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('project_name', project_name);

      // Call background endpoint (returns immediately with task_id)
      const response = await fetch(`${API_BASE_URL}/api/extract_feature_details/background`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result; // Returns { task_id, status, message }
    } catch (error) {
      console.error('Error extracting features:', error);
      throw error;
    }
  },


  async getDetailedFeatures(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stored_features_with_details/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching detailed features:', error);
      throw error;
    }
  },

  async extractAttributes(projectId, project_name) {
    try {
      // For form data, we don't set Content-Type header as it will be set automatically with boundary
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type']; // Remove content-type to let browser set it with boundary

      // Create form data for the POST request
      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('project_name', project_name);

      // Call background endpoint (returns immediately with task_id)
      const response = await fetch(`${API_BASE_URL}/api/extract_attribute_details/background`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result; // Returns { task_id, status, message }
    } catch (error) {
      console.error('Error extracting attributes:', error);
      throw error;
    }
  },


  async getDetailedAttributes(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stored_attributes_with_details/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching detailed attributes:', error);
      throw error;
    }
  },

  async extractFeatureSubfeatureTitles(projectId, project_name) {
    try {
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type'];

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('project_name', project_name);

      // Call background endpoint
      const response = await fetch(`${API_BASE_URL}/api/extract_feature_subfeature_title/background`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result; // Returns { task_id, status, message }
    } catch (error) {
      console.error('Error extracting feature/subfeature titles:', error);
      throw error;
    }
  },

  async extractAttributeTitles(projectId, project_name) {
    try {
      const headers = { ...getAuthHeaders() };
      delete headers['Content-Type'];

      const formData = new FormData();
      formData.append('project_id', projectId);
      formData.append('project_name', project_name);

      // Call background endpoint
      const response = await fetch(`${API_BASE_URL}/api/extract_attributes_title/background`, {
        method: 'POST',
        headers: headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result; // Returns { task_id, status, message }
    } catch (error) {
      console.error('Error extracting attribute titles:', error);
      throw error;
    }
  },

  async getProjectTasks(projectId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/project/${projectId}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching project tasks:', error);
      throw error;
    }
  },

};